/**
 * A/B Test Service (HTTP Client)
 *
 * Handles all HTTP communication with the A/B testing backend API.
 * Provides methods for:
 * - Fetching active experiments
 * - Assigning users to variants
 * - Tracking events (single and batch)
 *
 * Part of: MVP 1.10 - A/B Testing & Experimentation Framework
 * Location: apps/web/src/services/abTestService.js
 * Created: 2025-10-31
 */

// Use VITE_API_URL for cross-origin requests in staging/production
// Development: http://localhost:3030, Staging: https://env-9b2d.outdoorguides.com
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';
const AB_TEST_ENDPOINT = `${API_BASE_URL}/api/v1/ab-tests`;

/**
 * HTTP Client for A/B Testing API
 */
class ABTestService {
  /**
   * Get all active experiments
   *
   * @returns {Promise<Array>} - Array of active experiments
   */
  async getActiveExperiments() {
    try {
      const response = await fetch(`${AB_TEST_ENDPOINT}/active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include cookies for session
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch experiments: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.debug('[ABTestService] Could not fetch active experiments (API may be starting):', error.message);
      return [];
    }
  }

  /**
   * Assign user to experiment variant
   *
   * This method is idempotent - calling it multiple times for the same
   * user+experiment will always return the same variant.
   *
   * Supports both authenticated users (via userId) and anonymous users (via sessionId).
   *
   * @param {string} userId - User UUID (optional if sessionId provided)
   * @param {string} experimentName - Experiment name
   * @param {string} sessionId - Session ID for anonymous users (optional if userId provided)
   * @returns {Promise<Object>} - { experimentId, experimentName, variant, isNewAssignment }
   */
  async assignUser(userId, experimentName, sessionId = null) {
    try {
      const response = await fetch(`${AB_TEST_ENDPOINT}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ userId, sessionId, experimentName })
      });

      if (!response.ok) {
        throw new Error(`Failed to assign user: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle graceful "no experiment" response
      if (data.noExperiment) {
        console.info('[ABTestService] No experiment found:', experimentName);
        return {
          noExperiment: true,
          variant: 'control',  // Default to control
          experimentId: null,
          experimentName: experimentName,
          isDefault: true,
          isNewAssignment: false
        };
      }

      // Automatically track assignment event if this is a new assignment
      if (data.variant && data.isNewAssignment) {
        // Fire-and-forget event tracking (don't await)
        this.trackEvent({
          experimentName,
          userId: userId || null,
          sessionId: sessionId || null,
          eventType: 'assignment',
          variant: data.variant
        }).catch(err => {
          console.warn('[ABTestService] Failed to track assignment event:', err);
        });
      }

      return data;
    } catch (error) {
      console.error('[ABTestService] Error assigning user:', error);
      return { variant: null, error: error.message }; // Return null variant on error
    }
  }

  /**
   * Track single event
   *
   * @param {Object} event - Event object
   * @param {string} event.experimentName - Experiment name
   * @param {string} event.userId - User UUID
   * @param {string} event.eventType - Event type (exposure, click, conversion, etc.)
   * @param {string} event.variant - Variant name
   * @param {Object} event.properties - Optional JSONB properties
   * @param {string} event.sessionId - Optional session UUID
   * @returns {Promise<Object>} - Tracked event
   */
  async trackEvent(event) {
    try {
      const response = await fetch(`${AB_TEST_ENDPOINT}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ events: [event] }) // Wrap in array for batch endpoint
      });

      if (!response.ok) {
        throw new Error(`Failed to track event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ABTestService] Error tracking event:', error);
      throw error;
    }
  }

  /**
   * Track multiple events in batch (optimized)
   *
   * @param {Array<Object>} events - Array of event objects
   * @returns {Promise<Object>} - { success: true, eventsTracked: number }
   */
  async trackEventsBatch(events) {
    try {
      const response = await fetch(`${AB_TEST_ENDPOINT}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ events })
      });

      if (!response.ok) {
        throw new Error(`Failed to track batch events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ABTestService] Error tracking batch events:', error);
      throw error;
    }
  }

  /**
   * Track exposure event (convenience method)
   *
   * Call this when a user is exposed to an experiment variant.
   * This should be called once per page view or component mount.
   *
   * @param {string} experimentName - Experiment name
   * @param {string} userId - User UUID (optional if sessionId provided)
   * @param {string} variant - Variant name
   * @param {Object} properties - Optional properties (e.g., page, component)
   * @param {string} sessionId - Session ID for anonymous users (optional if userId provided)
   * @returns {Promise<void>}
   */
  async trackExposure(experimentName, userId, variant, properties = {}, sessionId = null) {
    // Fire-and-forget (don't block UI)
    this.trackEvent({
      experimentName,
      userId: userId || null,
      sessionId: sessionId || null,
      eventType: 'exposure',
      variant,
      properties
    }).catch(err => {
      console.warn('[ABTestService] Failed to track exposure:', err);
    });
  }

  /**
   * Track click event (convenience method)
   *
   * @param {string} experimentName - Experiment name
   * @param {string} userId - User UUID
   * @param {string} variant - Variant name
   * @param {Object} properties - Optional properties (e.g., element, action)
   * @returns {Promise<void>}
   */
  async trackClick(experimentName, userId, variant, properties = {}) {
    // Fire-and-forget
    this.trackEvent({
      experimentName,
      userId,
      eventType: 'click',
      variant,
      properties
    }).catch(err => {
      console.warn('[ABTestService] Failed to track click:', err);
    });
  }

  /**
   * Track conversion event (convenience method)
   *
   * @param {string} experimentName - Experiment name
   * @param {string} userId - User UUID
   * @param {string} variant - Variant name
   * @param {Object} properties - Optional properties (e.g., value, currency)
   * @returns {Promise<void>}
   */
  async trackConversion(experimentName, userId, variant, properties = {}) {
    // Fire-and-forget
    this.trackEvent({
      experimentName,
      userId,
      eventType: 'conversion',
      variant,
      properties
    }).catch(err => {
      console.warn('[ABTestService] Failed to track conversion:', err);
    });
  }

  /**
   * Track filter change event (for search experiments)
   *
   * @param {string} experimentName - Experiment name
   * @param {string} userId - User UUID
   * @param {string} variant - Variant name
   * @param {Object} properties - Filter properties (filterType, value, etc.)
   * @returns {Promise<void>}
   */
  async trackFilterChange(experimentName, userId, variant, properties = {}) {
    // Fire-and-forget
    this.trackEvent({
      experimentName,
      userId,
      eventType: 'filter_change',
      variant,
      properties
    }).catch(err => {
      console.warn('[ABTestService] Failed to track filter change:', err);
    });
  }

  /**
   * Get experiment statistics (admin only)
   *
   * @param {string} experimentId - Experiment UUID
   * @returns {Promise<Object>} - Experiment stats
   */
  async getExperimentStats(experimentId) {
    try {
      const response = await fetch(`${AB_TEST_ENDPOINT}/${experimentId}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ABTestService] Error fetching stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
const abTestService = new ABTestService();
export default abTestService;
